/**
 * Helper functions for citation handling
 */

/**
 * Normalize a filename / citation string for forgiving comparison:
 *  - lowercase
 *  - trim
 *  - collapse runs of whitespace, treat space and underscore as equivalent
 *  - drop a trailing .pdf extension (LLM sometimes adds/omits it)
 *
 * The LLM frequently produces citations that differ from the indexed
 * sourcefile in tiny ways: an extra space, a lowercase vs uppercase,
 * "_" rendered as " ", a trailing `.pdf`. Strict equality misses all of
 * these and the citation pill silently disappears from the UI.
 */
const normalizeFilename = (s: string): string =>
    s
        .toLowerCase()
        .trim()
        .replace(/\.pdf$/i, "")
        .replace(/[_\s]+/g, " ")
        .replace(/\s+/g, " ");

/**
 * Find a search result by citation/document ID.
 * Handles:
 * - Exact ID match
 * - Partial ID match (for chunks)
 * - Source filename match (case-insensitive, space/underscore tolerant)
 * - .txt file references
 * - .pdf extension mismatches
 */
export const findSearchResult = (citation: string, searchResults: any[]): any => {
    if (!citation || !searchResults?.length) return null;

    const normalizedCitation = normalizeFilename(citation);

    return searchResults.find((item: any) => {
        // Exact match on ID
        if (item.id === citation) return true;

        // Partial match on ID (handles chunk references)
        if (item.id?.startsWith(citation)) return true;

        // Match on sourcefile (exact)
        if (item.sourcefile === citation) return true;

        // Match on sourcefile (normalized: case + whitespace + underscore tolerant)
        if (item.sourcefile && normalizeFilename(item.sourcefile) === normalizedCitation) {
            return true;
        }

        // Handle .txt file references
        if (item.sourcefile && citation.includes(item.sourcefile.replace(".txt", ""))) {
            return true;
        }

        // If LLM cited the ID with .pdf extension, match to sourcefile
        // e.g., LLM says [0147ZEXLEA2LHIW2DMGFCYY2NYXOCASW6E_chunk_1.pdf]
        // but item.id is "0147ZEXLEA2LHIW2DMGFCYY2NYXOCASW6E_chunk_1"
        if (citation.endsWith('.pdf') && item.id === citation.replace('.pdf', '')) return true;

        return false;
    });
};
